import React, { useState, useMemo, memo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const ListView = ({ leads, activeFilter }) => {
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedLeads, setExpandedLeads] = useState(new Set());

  const toggleExpanded = (leadId) => {
    setExpandedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  // Memoized sort: must be declared before the early return so hooks are always
  // called in the same order. Only recalculates when leads, sortField, or sortDirection change.
  const sortedLeads = useMemo(() => {
    if (!leads || leads.length === 0) return [];
    return [...leads].sort((a, b) => {
      if (!sortField) return 0;

      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle opportunity_score or numeric fields
      if (sortField === 'opportunity_score' || typeof aVal === 'number') {
        aVal = aVal || 0;
        bVal = bVal || 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle string fields
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [leads, sortField, sortDirection]);

  if (!leads || leads.length === 0) {
    return (
      <div className="h-full w-full overflow-y-auto p-4 md:p-8 pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <p className="text-gray-500 text-lg">
              {activeFilter === 'ai_picks'
                ? 'No AI recommendations yet. Generate a briefing to get personalized picks.'
                : 'No leads to display for the current filter.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (status, category) => {
    // Overrides for urgency
    if (status === 'at_risk') return 'bg-orange-100 text-orange-700';
    if (status === 'churned') return 'bg-red-100 text-red-700';

    // Category themes
    switch (category) {
      case 'current_customers': return 'bg-emerald-100 text-emerald-700';
      case 'won': return 'bg-green-100 text-green-700';
      case 'leads': return 'bg-blue-100 text-blue-700';
      case 'planned_visits': return 'bg-purple-100 text-purple-700';
      case 'inactive': return 'bg-gray-100 text-gray-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  const getPriorityEmoji = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return '🔥🔥🔥';
      case 'medium': return '⚡⚡';
      case 'low': return '❄️';
      default: return '—';
    }
  };


  // Full view with proper mobile scrolling
  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      {/* Container with safe padding for mobile footer and TopBar */}
      <div className="max-w-6xl mx-auto p-4 md:p-8 pt-20 md:pt-24 pb-24">
        {/* Title - responsive */}
        <h2 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 text-gray-900">All Leads</h2>

        {/* Table wrapper with shadow */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          {/* Desktop: Full table, Mobile: Scrollable table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th
                    className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    Lead {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('opportunity_score')}
                  >
                    Smart Score {sortField === 'opportunity_score' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('status')}
                  >
                    Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-700">
                    Priority
                  </th>
                  <th className="hidden md:table-cell px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    Summary
                  </th>
                  <th className="px-2 md:px-4 py-3 md:py-4 text-center text-xs md:text-sm font-semibold text-gray-700">

                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedLeads.map((lead) => {
                  const isExpanded = expandedLeads.has(lead.global_id);
                  return (
                    <React.Fragment key={lead.global_id}>
                      <tr
                        className={`hover:bg-gray-50 transition-colors cursor-pointer ${isExpanded ? 'bg-blue-50' : ''}`}
                        onClick={() => toggleExpanded(lead.global_id)}
                      >
                        <td className="px-4 md:px-6 py-3 md:py-4">
                          <div className="font-semibold text-sm md:text-base text-gray-900">
                            {lead.name || `Entity #${lead.id}`}
                          </div>
                          <div className="text-xs md:text-sm text-gray-500 truncate max-w-[150px] md:max-w-none">
                            {lead.address || 'No address'}
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-3 md:py-4">
                          <div className="flex items-center gap-2">
                            <div className="text-lg md:text-xl font-bold text-gray-900">
                              {lead.opportunity_score ? Math.round(lead.opportunity_score * 100) : (lead.value ? '—' : '0')}
                            </div>
                            <div className="hidden md:block flex-1 bg-gray-200 rounded-full h-2 w-16 md:w-24">
                              <div
                                className={`h-2 rounded-full ${(lead.opportunity_score * 100) >= 80 ? 'bg-emerald-500' :
                                  (lead.opportunity_score * 100) >= 50 ? 'bg-blue-500' :
                                    'bg-gray-400'
                                  }`}
                                style={{ width: `${(lead.opportunity_score || 0) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-3 md:py-4">
                          <span className={`px-2 md:px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(lead.status, lead.category)}`}>
                            {(lead.status || lead.category || 'potential').toUpperCase().replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 md:px-6 py-3 md:py-4">
                          <div className="flex items-center gap-1 text-base md:text-lg">
                            {getPriorityEmoji(lead.priority)}
                          </div>
                        </td>
                        <td className="hidden md:table-cell px-6 py-4">
                          <div className="text-sm text-gray-600 max-w-md line-clamp-2">
                            {lead.summary || 'N/A'}
                          </div>
                        </td>
                        <td className="px-2 md:px-4 py-3 md:py-4">
                          <button
                            className="p-1 rounded-lg hover:bg-gray-200 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpanded(lead.global_id);
                            }}
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-500" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-500" />
                            )}
                          </button>
                        </td>
                      </tr>
                      {/* Expandable Summary Row */}
                      {isExpanded && (
                        <tr className="bg-gradient-to-r from-blue-50 to-indigo-50">
                          <td colSpan={6} className="px-4 md:px-6 py-4">
                            <div className="space-y-3">
                              {/* Full Summary */}
                              <div>
                                <div className="font-semibold text-gray-700 text-sm mb-2 flex items-center gap-2">
                                  <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">AI Summary</span>
                                </div>
                                <p className="text-sm text-gray-700 leading-relaxed">
                                  {lead.summary || 'No summary available for this lead.'}
                                </p>
                              </div>

                              {/* Key Metrics */}
                              <div className="flex flex-wrap gap-2 pt-2 border-t border-blue-200">
                                {lead.forecasted_sales && (
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    💰 ¥{lead.forecasted_sales.toLocaleString()} potential
                                  </span>
                                )}
                                {lead.industry && (
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                    🏢 {lead.industry}
                                  </span>
                                )}
                                {lead.type && (
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    📍 {lead.type}
                                  </span>
                                )}
                                {lead.last_contacted_at && (
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    📅 Last contact: {new Date(lead.last_contacted_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Shallow prop memo: skips re-render when leads reference and activeFilter haven't changed.
// sortField/sortDirection are internal state so they don't factor into this check.
export default memo(ListView);